import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Tracking GA4 + Microsoft Clarity.
 * Carregamento condicional ao consentimento de cookies (LGPD/GDPR).
 *
 * Eventos customizados:
 *  - page_view (auto on route change)
 *  - package_viewed
 *  - booking_started
 *  - booking_step_completed (1..4)
 *  - payment_succeeded
 *  - contact_submitted
 *  - language_changed
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

const COOKIE_KEY = 'dp_cookie_consent';

export function getConsent(): { analytics: boolean; marketing: boolean } | null {
  try {
    const raw = window.localStorage.getItem(COOKIE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setConsent(value: { analytics: boolean; marketing: boolean }): void {
  window.localStorage.setItem(COOKIE_KEY, JSON.stringify(value));
  if (value.analytics) loadGA4();
  if (value.analytics) loadClarity();
}

function loadGA4(): void {
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  if (!id || window.gtag) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) { window.dataLayer!.push(args); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id, { anonymize_ip: true });
}

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };

function loadClarity(): void {
  const id = import.meta.env.VITE_CLARITY_PROJECT_ID;
  if (!id || window.clarity) return;
  const w = window as Window & { clarity: ClarityFn };
  if (!w.clarity) {
    const fn: ClarityFn = function (...args: unknown[]) {
      (fn.q = fn.q || []).push(args);
    };
    w.clarity = fn;
  }
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://www.clarity.ms/tag/' + id;
  const y = document.getElementsByTagName('script')[0];
  y?.parentNode?.insertBefore(t, y);
}

export function track(event: string, params?: Record<string, unknown>): void {
  if (window.gtag) window.gtag('event', event, params ?? {});
}

export function useTracking(): void {
  const location = useLocation();
  const initialized = useRef(false);

  // Carrega scripts no primeiro consentimento
  useEffect(() => {
    if (initialized.current) return;
    const c = getConsent();
    if (c?.analytics) {
      loadGA4();
      loadClarity();
    }
    initialized.current = true;
  }, []);

  // Page view em mudanca de rota
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
        page_search: location.search,
      });
    }
  }, [location.pathname, location.search]);
}
